import { Button } from '@cian/ui-kit/button';
import { IconList16 } from '@cian/ui-kit/icons';
import cn from 'classnames';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './ListingModalContainer.css';
import { getCloseButtonExtraClassName } from './utils/getCloseButtonExtraClassName';
import { trackListingButtonClick } from '../../../../packages/Listing';
// eslint-disable-next-line import/no-restricted-paths
import { RedesignedFiltersPanelId } from '../../../FiltersMobile/containers/RedesignedFiltersPanel';
import {
  closeAndResetListingModal,
  EListingModalMode,
  EOffersFetchingMode,
  toggleModal,
} from '../../../common/actions/listingModal';
import { loadListingOffers } from '../../../common/actions/listingOffers';
import { ETestId } from '../../../common/constants/testId';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { ChatModalContainer } from '../../../common/containers/ChatModalContainer';
import { PortalWrapper } from '../../../common/containers/PortalWrapper';
import { SwipeModal, useMaxModalHeight } from '../../../common/containers/SwipeModal';
import { selectIsModalOpen, selectOffersFetchingMode } from '../../../common/selectors/listingModal';
import {
  selectIsOffersLoading,
  selectListingOffers,
  selectListingSeoData,
} from '../../../common/selectors/listingOffers';
import { selectQueryStringWithQMark } from '../../../common/selectors/queryString';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { IListingOffersState } from '../../../common/types/listingOffers';
import { IApplicationState, TThunkDispatch } from '../../../common/types/redux';
import { ModalHeader } from '../../components';
import { trackListingShow } from '../../tracking';
import { trackListingClose } from '../../tracking/trackListingClose';
import { trackListingSwipeClose } from '../../tracking/trackListingSwipeClose';
import { ListingMobileContainer } from '../ListingMobile';

export const ListingModalContainer = () => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const show = useSelector(selectIsModalOpen);
  const modalMode = useSelector((state: IApplicationState) => state.listingModal.mode);
  const isHalfScreenMode = React.useMemo(() => modalMode === EListingModalMode.HALF_SCREEN, [modalMode]);
  const offersFetchingMode = useSelector(selectOffersFetchingMode);
  const isSingleOfferMode = offersFetchingMode === EOffersFetchingMode.SINGLE;
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const anchorDomId = RedesignedFiltersPanelId;
  const seoData = useSelector(selectListingSeoData);
  const isSeoEnabled = config.get<boolean>('dailyrent.seo.Enabled');
  const seoTitle = isSeoEnabled ? seoData.h1 : null;
  const listingOffersState = useSelector<IApplicationState, IListingOffersState>(state => state.listingOffers);
  const hasListingOffers = Boolean(listingOffersState.offers?.length);
  const queryString = useSelector(selectQueryStringWithQMark);
  const offers = useSelector(selectListingOffers);
  const isLoading = useSelector(selectIsOffersLoading);

  const maxHeight = useMaxModalHeight(anchorRef);

  // если вид 50/50 или превью в эксперименте с редизайном
  const shouldListenSwipeDown = React.useMemo(
    () => isHalfScreenMode || isSingleOfferMode,
    [isHalfScreenMode, isSingleOfferMode],
  );

  const onClose = useCallback(() => {
    dispatch(closeAndResetListingModal());
  }, [closeAndResetListingModal]);

  const onCloseButtonClick = React.useCallback(() => {
    if (!isSingleOfferMode) {
      trackListingClose(jsonQuery, queryString);
    }
    onClose();
  }, [jsonQuery, isSingleOfferMode, queryString, onClose]);

  const onSwipeClose = useCallback(() => {
    trackListingSwipeClose({ jsonQuery, listingType: isSingleOfferMode ? 'map' : 'listing' });
    onClose();
  }, [isSingleOfferMode, jsonQuery, onClose]);

  const onOpen = React.useCallback(() => {
    trackListingButtonClick(jsonQuery);

    if (!hasListingOffers) {
      dispatch(loadListingOffers({ offersFetchingMode: EOffersFetchingMode.ALL })).then(
        trackListingShow('all_objects_on_map'),
      );
    } else {
      trackListingShow('all_objects_on_map')({
        offers: listingOffersState.offers,
        mlRanking: listingOffersState.mlRanking || undefined,
        jsonQuery,
        queryString,
      });
    }

    dispatch(
      toggleModal({
        open: true,
        mode: EListingModalMode.FULL_SCREEN,
        offersFetchingMode: EOffersFetchingMode.ALL,
      }),
    );
  }, [dispatch, jsonQuery, hasListingOffers, listingOffersState, queryString]);

  const onFullscreenToggle = React.useCallback(() => {
    dispatch(toggleModal({ open: true, mode: EListingModalMode.FULL_SCREEN }));
  }, [dispatch]);

  React.useEffect(() => {
    anchorRef.current = document.getElementById(anchorDomId);
  }, []);

  const onHalfModeToggleToFullMode = React.useCallback(() => {
    if (isLoading) {
      return;
    }

    trackListingShow('all_objects_on_map')({
      offers: listingOffersState.offers,
      mlRanking: listingOffersState.mlRanking || undefined,
      jsonQuery,
      queryString,
    });

    onFullscreenToggle();
  }, [jsonQuery, listingOffersState.mlRanking, listingOffersState.offers, onFullscreenToggle, queryString, isLoading]);

  const listingButtonClickHandler = onOpen;
  const extraModalClassname = cn(styles['modal'], isSingleOfferMode && styles['single_offer_mode']);

  const extraButtonClassName = getCloseButtonExtraClassName(offers[0], isSingleOfferMode);

  return (
    <>
      <div className={styles['toggle']}>
        <Button
          onClick={listingButtonClickHandler}
          theme="fill_white_primary"
          size="M"
          beforeIcon={<IconList16 color="black_100" />}
          data-testid={ETestId.ListingButton}
        >
          Список
        </Button>
      </div>
      <PortalWrapper>
        <SwipeModal
          className={extraModalClassname}
          maxHeight={isHalfScreenMode ? maxHeight / 2 : maxHeight}
          fixHeight={!isSingleOfferMode}
          show={show}
          header={!isLoading && !isSingleOfferMode && hasListingOffers && <ModalHeader title={seoTitle} />}
          halfScreen={isHalfScreenMode}
          onClose={onClose}
          onCloseButtonClick={onCloseButtonClick}
          closeButtonClassName={cn(extraButtonClassName && styles[extraButtonClassName])}
          onFullscreenToggle={onFullscreenToggle}
          hasCloseButton={!isLoading && !isSingleOfferMode}
          testId={ETestId.ListingMobile}
          onSwipeUp={isHalfScreenMode ? onHalfModeToggleToFullMode : undefined}
          onSwipeDown={onSwipeClose}
          shouldListenSwipeDown={shouldListenSwipeDown}
        >
          <ListingMobileContainer />
        </SwipeModal>
      </PortalWrapper>

      <ChatModalContainer />
    </>
  );
};
